/**
 * core-field-option-labels.ts — nazwy kodów list zamkniętych pól systemowych (kody: rejestr
 * `core-field-domains` w `@saas/shared`). Jedno miejsce mapowania (kartoteka, pole, kod) → nazwa
 * dla edytora wartości domyślnej; formularze, kolumny i filtry list używają tych samych funkcji
 * (dla dokumentów i faktur — z `lib/documents/*-select-labels.ts` i `ksef-jpk-markers.ts`).
 *
 * Klucze budowane z kodu są martwym polem skanera `check:i18n` — strażnikiem jest test obok
 * (każdy kod każdego pola z rejestru ma prawdziwe tłumaczenie w `pl.json`). Kod spoza listy (dane
 * historyczne) wraca surowy (`defaultValue`).
 */

import {
  contractDocumentStatusLabel,
  contractPeriodUnitLabel,
  contractStatusLabel,
  documentUrgencyLabel,
  letterStatusLabel,
  letterUrgencyLabel,
  standardDocumentStatusLabel,
} from '@/lib/documents/document-select-labels';
import {
  invoiceKindLabel,
  invoicePaymentMethodLabel,
  invoiceStatusLabel,
} from '@/lib/documents/invoice-select-labels';
import {
  documentReceiptChannelLabel,
  jpkDocumentMarkerLabel,
  ksefIssueModeLabel,
  purchaseEvidenceMarkerLabel,
} from '@/lib/documents/ksef-jpk-markers';
import type { CoreColumnTranslateFn } from '@/lib/ext-list-columns';

export type CodeLabelFn = (code: string, t: CoreColumnTranslateFn) => string;

const byPrefix =
  (prefix: string): CodeLabelFn =>
  (code, t) =>
    t(`${prefix}.${code}`, { defaultValue: code });

/** Kod jest nazwą (waluta: „PLN”, „EUR”). */
const codeAsLabel: CodeLabelFn = (code) => code;

/** Język rekordu (kontrahent, kontakt, użytkownik) — wspólne nazwy „Polski” / „Angielski”. */
export const recordLanguageLabel: CodeLabelFn = byPrefix('tenant.contractors.detail.language');
export const contractorObjectTypeLabel: CodeLabelFn = byPrefix(
  'tenant.contractors.detail.objectType',
);
export const contractorStatusLabel: CodeLabelFn = byPrefix('tenant.contractors.detail.status');
export const contractorVatStatusLabel: CodeLabelFn = byPrefix(
  'tenant.contractors.detail.vatStatus',
);
export const contactPreferredChannelLabel: CodeLabelFn = byPrefix(
  'tenant.contractorContacts.preferredChannel',
);
export const employeeIdDocumentTypeLabel: CodeLabelFn = byPrefix('tenant.employees.idDocumentType');
export const employmentTypeLabel: CodeLabelFn = byPrefix('tenant.employees.employmentType');

const saleInvoiceKindLabel: CodeLabelFn = (code, t) =>
  invoiceKindLabel(code, t, 'tenant.saleInvoices');

const INVOICE_COMMON: Readonly<Record<string, CodeLabelFn>> = {
  currency_code: codeAsLabel,
  status: invoiceStatusLabel,
};

const STANDARD_DOCUMENT: Readonly<Record<string, CodeLabelFn>> = {
  status: standardDocumentStatusLabel,
  urgency: documentUrgencyLabel,
};

const LABELS: Readonly<Record<string, Readonly<Record<string, CodeLabelFn>>>> = {
  contractors: {
    object_type: contractorObjectTypeLabel,
    status: contractorStatusLabel,
    vat_status: contractorVatStatusLabel,
    preferred_language: recordLanguageLabel,
  },
  contractor_contacts: {
    language: recordLanguageLabel,
    preferred_channel: contactPreferredChannelLabel,
  },
  employees: {
    id_document_type: employeeIdDocumentTypeLabel,
    employment_type: employmentTypeLabel,
  },
  users: {
    language: recordLanguageLabel,
  },
  purchase_invoices: {
    ...INVOICE_COMMON,
    purchase_evidence_marker: purchaseEvidenceMarkerLabel,
    jpk_document_marker: jpkDocumentMarkerLabel,
    ksef_issue_mode: ksefIssueModeLabel,
    document_receipt_channel: documentReceiptChannelLabel,
  },
  sale_invoices: {
    ...INVOICE_COMMON,
    document_type: saleInvoiceKindLabel,
    urgency: documentUrgencyLabel,
    payment_method: invoicePaymentMethodLabel,
    ksef_issue_mode: ksefIssueModeLabel,
  },
  letters: {
    status: letterStatusLabel,
    urgency: letterUrgencyLabel,
  },
  contracts: {
    status: contractDocumentStatusLabel,
    urgency: documentUrgencyLabel,
    contract_status: contractStatusLabel,
    period_duration_unit: contractPeriodUnitLabel,
  },
  commercial: STANDARD_DOCUMENT,
  internal: STANDARD_DOCUMENT,
  specjalne: STANDARD_DOCUMENT,
};

/** Funkcja nazwy kodów pola systemowego (`null` — pole bez listy zamkniętej w rejestrze). */
export function coreFieldLabelFn(moduleSlug: string, fieldSlug: string): CodeLabelFn | null {
  return LABELS[moduleSlug]?.[fieldSlug] ?? null;
}

/** Nazwa kodu pola systemowego; pole albo kod spoza rejestru — surowy kod. */
export function coreFieldOptionLabel(
  moduleSlug: string,
  fieldSlug: string,
  code: string,
  t: CoreColumnTranslateFn,
): string {
  const labelOf = coreFieldLabelFn(moduleSlug, fieldSlug);
  return labelOf ? labelOf(code, t) : code;
}

/** Opcje listy wyboru `{ value, label }` dla kodów pola systemowego. */
export function coreFieldOptions(
  moduleSlug: string,
  fieldSlug: string,
  codes: readonly string[],
  t: CoreColumnTranslateFn,
): Array<{ value: string; label: string }> {
  return codes.map((code) => ({
    value: code,
    label: coreFieldOptionLabel(moduleSlug, fieldSlug, code, t),
  }));
}
